import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, AlertTriangle, FileText, Clock, CheckCircle, XCircle, Download, ExternalLink } from 'lucide-react';
import { useBatchHistory } from '@/hooks/useBatchHistory';
import type { BatchRecord } from '@/services/batchHistoryAPI';

type BatchListProps = Record<string, never>

const EmptyState: React.FC = () => (
  <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
    <FileText className="h-10 w-12 text-slate-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-slate-900 mb-2">No batches available</h3>
    <p className="text-slate-600 mb-6">
      No batch processing history found. Connect to backend API to see batch data.
    </p>
    <Link
      to="/admin/batches/new"
      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
    >
      <Plus className="h-4 w-4 mr-2" />
      Create First Batch
    </Link>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    processing: { color: 'bg-slate-100 text-blue-800', icon: Clock },
    completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
  };

  const { color, icon: Icon } = config[status as keyof typeof config] || config.pending;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3 mr-1" />
      {status}
    </span>
  );
};

export const BatchListPage: React.FC<BatchListProps> = () => {
  const { batches, loading, error } = useBatchHistory();
  const [filteredBatches, setFilteredBatches] = useState<BatchRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    // Filter batches based on search and status
    let filtered = batches;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(batch => 
        batch.id.toLowerCase().includes(query) ||
        batch.filename.toLowerCase().includes(query) ||
        batch.operation.toLowerCase().includes(query) ||
        batch.adminEmail.toLowerCase().includes(query)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(batch => batch.status === statusFilter);
    }

    setFilteredBatches(filtered);
  }, [batches, searchQuery, statusFilter]);

  if (error) {
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Batch Management</h1>
            <p className="text-slate-600 mt-1">Manage and monitor batch processing jobs</p>
          </div>
          <Link
            to="/admin/batches/new"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Batch
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">Error Loading Batches</h3>
              <p className="text-red-700 mt-1">Unable to connect to batch processing API. Please check your configuration.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Batch Management</h1>
          <p className="text-slate-600 mt-1">Manage and monitor batch processing jobs</p>
        </div>
        <Link
          to="/admin/batches/new"
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Batch
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search batches by ID, filename, or operation..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <button className="inline-flex items-center px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
              <Filter className="h-4 w-4 mr-1" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Batch List */}
      {loading ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading batches...</p>
        </div>
      ) : filteredBatches.length === 0 ? (
        batches.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <Search className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">No batches found matching your search criteria.</p>
          </div>
        )
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-medium text-slate-900">Recent Batches</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Batch ID
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    File
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Operation
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Records
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredBatches.map((batch) => (
                  <tr key={batch.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-slate-900">{batch.id}</div>
                      <div className="text-slate-500 text-xs">{new Date(batch.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-slate-400" />
                        {batch.filename}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-blue-800">
                        {batch.operation}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={batch.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div>{batch.successfulRecords}/{batch.totalRecords}</div>
                      <div className="text-xs text-slate-500">
                        {batch.totalRecords > 0 ? Math.round((batch.successfulRecords / batch.totalRecords) * 100) : 0}% success
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      <div>{new Date(batch.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs">{new Date(batch.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        {batch.csvDownloadUrl && (
                          <button
                            onClick={() => window.open(batch.csvDownloadUrl, '_blank')}
                            className="text-green-600 hover:text-green-900 transition-colors"
                            title="Download CSV"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        {batch.jsonDownloadUrl && (
                          <button
                            onClick={() => window.open(batch.jsonDownloadUrl, '_blank')}
                            className="text-slate-600 hover:text-slate-900 transition-colors"
                            title="Download JSON"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          batch.emailSent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {batch.emailSent ? '📧 Sent' : 'No Email'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
